import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { WorkstationLane } from "../../lib/workstation-lanes.ts";
import { type EvWorkstationTourStep, getVisibleSteps } from "./ev-workstation-tour-steps.ts";
import { setEvWorkstationTourCompleted } from "./tour-storage.ts";
import {
  findScrollableAncestors,
  getFirstVisibleTarget,
  nextFrame,
  rectWithPadding,
} from "./use-tour-geometry.ts";

export interface EvWorkstationTourProps {
  open: boolean;
  onClose: () => void;

  viewMode: "card" | "table";
  setViewMode: (mode: "card" | "table") => void;

  lane: WorkstationLane;
  setLane: (lane: WorkstationLane) => void;

  selectedProjectId: string | null;
  setSelectedProjectId: (projectId: string | null) => void;

  hasProjects: boolean;
}

interface EvTourInitialState {
  viewMode: "card" | "table";
  lane: WorkstationLane;
  selectedProjectId: string | null;
  windowScrollY: number;
  queueScrollTop: number | null;
  cardScrollTop: number | null;
  actionDockScrollTop: number | null;
  openDetails: Array<{ selector: string; open: boolean }>;
  activeElement: HTMLElement | null;
}

export function captureInitialState(
  viewMode: "card" | "table",
  lane: WorkstationLane,
  selectedProjectId: string | null
): EvTourInitialState {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return {
      viewMode,
      lane,
      selectedProjectId,
      windowScrollY: 0,
      queueScrollTop: null,
      cardScrollTop: null,
      actionDockScrollTop: null,
      openDetails: [],
      activeElement: null,
    };
  }

  const queueList = document.querySelector(".ev-v2-queue-list");
  const card = document.querySelector(".ev-workstation-v2__card");
  const actionDock = document.querySelector(".ev-workstation-v2__actions");
  const detailsNodes = Array.from(document.querySelectorAll("details[data-ev-tour]"));

  return {
    viewMode,
    lane,
    selectedProjectId,
    windowScrollY: window.scrollY,
    queueScrollTop: queueList instanceof HTMLElement ? queueList.scrollTop : null,
    cardScrollTop: card instanceof HTMLElement ? card.scrollTop : null,
    actionDockScrollTop: actionDock instanceof HTMLElement ? actionDock.scrollTop : null,
    openDetails: detailsNodes
      .map((node) => {
        const tourAttr = node.getAttribute("data-ev-tour");
        return tourAttr
          ? { selector: `[data-ev-tour="${tourAttr}"]`, open: (node as HTMLDetailsElement).open }
          : null;
      })
      .filter((d): d is { selector: string; open: boolean } => d !== null),
    activeElement: document.activeElement instanceof HTMLElement ? document.activeElement : null,
  };
}

export function restoreTourDetails(openDetails: EvTourInitialState["openDetails"]) {
  if (typeof document === "undefined") return;
  for (const detail of openDetails) {
    const node = document.querySelector(detail.selector);
    if (node instanceof HTMLDetailsElement) {
      node.open = detail.open;
    }
  }
}

export function restoreTourState(
  state: EvTourInitialState,
  setters: {
    setViewMode: (mode: "card" | "table") => void;
    setLane: (lane: WorkstationLane) => void;
    setSelectedProjectId: (id: string | null) => void;
  }
) {
  setters.setViewMode(state.viewMode);
  setters.setLane(state.lane);
  setters.setSelectedProjectId(state.selectedProjectId);

  if (typeof window !== "undefined") {
    window.scrollTo({ top: state.windowScrollY, behavior: "auto" });
  }

  const queueList = document.querySelector(".ev-v2-queue-list");
  const card = document.querySelector(".ev-workstation-v2__card");
  const actionDock = document.querySelector(".ev-workstation-v2__actions");

  if (queueList instanceof HTMLElement && state.queueScrollTop !== null) {
    queueList.scrollTop = state.queueScrollTop;
  }
  if (card instanceof HTMLElement && state.cardScrollTop !== null) {
    card.scrollTop = state.cardScrollTop;
  }
  if (actionDock instanceof HTMLElement && state.actionDockScrollTop !== null) {
    actionDock.scrollTop = state.actionDockScrollTop;
  }

  restoreTourDetails(state.openDetails);

  if (typeof requestAnimationFrame !== "undefined") {
    requestAnimationFrame(() => {
      state.activeElement?.focus();
    });
  }
}

export async function prepareTourStep(
  targetStep: EvWorkstationTourStep,
  setters: {
    setViewMode: (mode: "card" | "table") => void;
    setLane: (lane: WorkstationLane) => void;
  }
): Promise<{ showed: boolean; rect: ReturnType<typeof rectWithPadding> | null }> {
  if (targetStep.requiredViewMode) setters.setViewMode(targetStep.requiredViewMode);
  if (targetStep.requiredLane) setters.setLane(targetStep.requiredLane);

  await nextFrame();
  await nextFrame();

  for (const selector of targetStep.closeDetails ?? []) {
    const details = document.querySelector(selector);
    if (details instanceof HTMLDetailsElement) {
      details.open = false;
    }
  }

  for (const selector of targetStep.openDetails ?? []) {
    const details = document.querySelector(selector);
    if (details instanceof HTMLDetailsElement) {
      details.open = true;
    }
  }

  await nextFrame();

  let target: HTMLElement | null = null;
  if (targetStep.target) {
    target = getFirstVisibleTarget(targetStep.target);
  }
  if (!target && targetStep.fallbackTarget) {
    target = getFirstVisibleTarget(targetStep.fallbackTarget);
  }

  if (!target) {
    return { showed: false, rect: null };
  }

  optimizeTargetScroll(target, targetStep);
  await nextFrame();

  const rect = rectWithPadding(target.getBoundingClientRect(), 10);
  return { showed: true, rect };
}

function useFocusTrap(containerRef: React.RefObject<HTMLElement | null>, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const container = containerRef.current;
      if (!container) return;

      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first && last) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last && first) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enabled, containerRef]);
}

function TourShade({
  rect,
}: {
  rect: { top: number; left: number; width: number; height: number };
}) {
  const { innerWidth, innerHeight } = window;
  return (
    <>
      <div
        className="ev-tour-shade"
        style={{ top: 0, left: 0, width: innerWidth, height: Math.max(0, rect.top) }}
      />
      <div
        className="ev-tour-shade"
        style={{
          top: rect.top,
          left: 0,
          width: Math.max(0, rect.left),
          height: rect.height,
        }}
      />
      <div
        className="ev-tour-shade"
        style={{
          top: rect.top,
          left: rect.left + rect.width,
          width: Math.max(0, innerWidth - rect.left - rect.width),
          height: rect.height,
        }}
      />
      <div
        className="ev-tour-shade"
        style={{
          top: rect.top + rect.height,
          left: 0,
          width: innerWidth,
          height: Math.max(0, innerHeight - rect.top - rect.height),
        }}
      />
    </>
  );
}

function isWindowScrollContainer(container: HTMLElement | Window): container is Window {
  return typeof window !== "undefined" && container === window;
}

function getScrollTop(container: HTMLElement | Window): number {
  return isWindowScrollContainer(container) ? window.scrollY : container.scrollTop;
}

function setScrollTop(container: HTMLElement | Window, value: number) {
  if (isWindowScrollContainer(container)) {
    window.scrollTo({ top: value, behavior: "auto" });
    return;
  }
  container.scrollTop = value;
}

function getMaxScrollTop(container: HTMLElement | Window): number {
  if (isWindowScrollContainer(container)) {
    return Math.max(
      0,
      (document.documentElement?.scrollHeight ?? window.innerHeight) - window.innerHeight
    );
  }
  return Math.max(0, container.scrollHeight - container.clientHeight);
}

function getScrollViewportHeight(container: HTMLElement | Window): number {
  return isWindowScrollContainer(container) ? window.innerHeight : container.clientHeight;
}

function getScrollViewportTop(container: HTMLElement | Window): number {
  return isWindowScrollContainer(container) ? 0 : container.getBoundingClientRect().top;
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function intersectionArea(
  a: { top: number; left: number; width: number; height: number },
  b: { top: number; left: number; width: number; height: number }
): number {
  const left = Math.max(a.left, b.left);
  const right = Math.min(a.left + a.width, b.left + b.width);
  const top = Math.max(a.top, b.top);
  const bottom = Math.min(a.top + a.height, b.top + b.height);
  return Math.max(0, right - left) * Math.max(0, bottom - top);
}

function buildScrollCandidates(target: HTMLElement, container: HTMLElement | Window): number[] {
  const current = getScrollTop(container);
  const max = getMaxScrollTop(container);
  const viewportHeight = getScrollViewportHeight(container);
  const viewportTop = getScrollViewportTop(container);
  const targetRect = target.getBoundingClientRect();
  const targetTopInContent = current + targetRect.top - viewportTop;
  const targetHeight = targetRect.height;
  const offsets = [
    24,
    viewportHeight * 0.15,
    viewportHeight * 0.3,
    viewportHeight * 0.5 - targetHeight / 2,
    viewportHeight * 0.7 - targetHeight,
    viewportHeight - targetHeight - 24,
  ];
  const candidates = new Set<number>([current]);

  for (const offset of offsets) {
    candidates.add(clampNumber(targetTopInContent - offset, 0, max));
  }

  const step = Math.max(24, Math.ceil(max / 96));
  for (let value = 0; value <= max; value += step) {
    candidates.add(value);
  }
  candidates.add(max);

  return Array.from(candidates).sort((a, b) => a - b);
}

function scoreTourPosition(
  targetRect: { top: number; left: number; width: number; height: number },
  placement: EvWorkstationTourStep["preferredPlacement"]
): number {
  const viewportRect = {
    top: 0,
    left: 0,
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  };
  const targetArea = Math.max(1, targetRect.width * targetRect.height);
  const visibleTargetRatio = intersectionArea(targetRect, viewportRect) / targetArea;
  const style = computePopoverStyle(targetRect, placement, 420, 560);

  if (typeof style.top !== "number" || typeof style.left !== "number") {
    return visibleTargetRatio * 2_000_000;
  }

  const modalHeight = typeof style.maxHeight === "number" ? style.maxHeight : 400;
  const modalWidth = typeof style.width === "number" ? style.width : 420;
  const modalRect = {
    top: style.top,
    left: style.left,
    width: modalWidth,
    height: modalHeight,
  };
  const overlap = intersectionArea(targetRect, modalRect);
  const modalVisibleRatio = Math.min(1, modalHeight / 560);
  const targetCenterDistance = Math.abs(
    targetRect.top + targetRect.height / 2 - viewportRect.height / 2
  );

  return (
    visibleTargetRatio * 2_000_000 +
    modalVisibleRatio * 1_000_000 -
    overlap * 10_000 -
    targetCenterDistance
  );
}

function optimizeTargetScroll(target: HTMLElement, step: EvWorkstationTourStep) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const scrollContainers = findScrollableAncestors(target);
  const container: HTMLElement | Window = scrollContainers[0] ?? window;
  const originalScrollTop = getScrollTop(container);
  let bestScrollTop = originalScrollTop;
  let bestScore = Number.NEGATIVE_INFINITY;

  for (const scrollTop of buildScrollCandidates(target, container)) {
    setScrollTop(container, scrollTop);
    const rect = rectWithPadding(target.getBoundingClientRect(), 10);
    const score = scoreTourPosition(rect, step.preferredPlacement);
    if (score > bestScore) {
      bestScore = score;
      bestScrollTop = scrollTop;
    }
  }

  setScrollTop(container, bestScrollTop);
}

function TourContent({ content }: { content: EvWorkstationTourStep["content"] }) {
  return (
    <div className="ev-tour-popover__sections">
      <div className="ev-tour-popover__section">
        <strong>What it is</strong>
        <p>{content.what}</p>
      </div>
      <div className="ev-tour-popover__section">
        <strong>What it does</strong>
        <p>{content.does}</p>
      </div>
      <div className="ev-tour-popover__section">
        <strong>Why it matters</strong>
        <p>{content.why}</p>
      </div>
      <div className="ev-tour-popover__section">
        <strong>How to use it well</strong>
        <p>{content.bestUse}</p>
      </div>
      <div className="ev-tour-popover__section">
        <strong>Outreach lens</strong>
        <p>{content.outreachContext}</p>
      </div>
      <div className="ev-tour-popover__section">
        <strong>Backend impact</strong>
        <p>{content.backendImpact}</p>
      </div>
      {content.avoid ? (
        <div className="ev-tour-popover__section ev-tour-popover__section--avoid">
          <strong>Avoid</strong>
          <p>{content.avoid}</p>
        </div>
      ) : null}
    </div>
  );
}

function computePopoverStyle(
  targetRect: { top: number; left: number; width: number; height: number } | null,
  placement: EvWorkstationTourStep["preferredPlacement"],
  popoverWidth: number,
  popoverHeight: number
): React.CSSProperties {
  const margin = 16;
  const gap = 18;
  const minWidth = 300;
  const minHeight = 220;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const maxViewportWidth = Math.max(240, vw - margin * 2);
  const desiredWidth = Math.min(popoverWidth, maxViewportWidth);
  const desiredHeight = Math.min(popoverHeight, vh - margin * 2);

  if (!targetRect || placement === "center") {
    return {
      top: "50%",
      left: "50%",
      width: desiredWidth,
      maxHeight: Math.min(vh - margin * 2, 680),
      transform: "translate(-50%, -50%)",
    };
  }

  const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;

  type CandidatePlacement = "top" | "right" | "bottom" | "left";
  type Candidate = {
    placement: CandidatePlacement;
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    score: number;
  };

  const candidates: Candidate[] = [];
  const preferredPlacement = placement;

  const addCandidate = (candidatePlacement: CandidatePlacement) => {
    if (candidatePlacement === "right" || candidatePlacement === "left") {
      const rawLeft =
        candidatePlacement === "right"
          ? targetRect.left + targetRect.width + gap
          : targetRect.left - gap - desiredWidth;
      const availableWidth =
        candidatePlacement === "right" ? vw - margin - rawLeft : targetRect.left - gap - margin;
      if (availableWidth < minWidth) return;

      const width = Math.min(desiredWidth, availableWidth);
      const left =
        candidatePlacement === "right" ? rawLeft : Math.max(margin, targetRect.left - gap - width);
      const maxHeight = Math.min(vh - margin * 2, 680);
      const top = clamp(targetCenterY - desiredHeight / 2, margin, vh - margin - desiredHeight);
      candidates.push({
        placement: candidatePlacement,
        top,
        left,
        width,
        maxHeight,
        score:
          width * maxHeight +
          (candidatePlacement === preferredPlacement ? 1_000_000 : 0) +
          (maxHeight >= popoverHeight ? 150_000 : 0),
      });
      return;
    }

    const isBottom = candidatePlacement === "bottom";
    const top = isBottom ? targetRect.top + targetRect.height + gap : margin;
    const bottomLimit = isBottom ? vh - margin : targetRect.top - gap;
    const maxHeight = bottomLimit - top;
    if (maxHeight < minHeight) return;

    const left = clamp(targetCenterX - desiredWidth / 2, margin, vw - margin - desiredWidth);
    candidates.push({
      placement: candidatePlacement,
      top: isBottom
        ? top
        : Math.max(margin, targetRect.top - gap - Math.min(desiredHeight, maxHeight)),
      left,
      width: desiredWidth,
      maxHeight: Math.min(maxHeight, 680),
      score:
        desiredWidth * maxHeight +
        (candidatePlacement === preferredPlacement ? 1_000_000 : 0) +
        (maxHeight >= popoverHeight ? 150_000 : 0),
    });
  };

  for (const candidatePlacement of ["right", "left", "bottom", "top"] as const) {
    addCandidate(candidatePlacement);
  }

  const best = candidates.sort((a, b) => b.score - a.score)[0];
  if (best) {
    return {
      top: best.top,
      left: best.left,
      width: best.width,
      maxHeight: best.maxHeight,
    };
  }

  return {
    top: margin,
    left: margin,
    width: maxViewportWidth,
    maxHeight: vh - margin * 2,
  };
}

function setRectIfChanged(
  setter: React.Dispatch<React.SetStateAction<ReturnType<typeof rectWithPadding> | null>>,
  next: ReturnType<typeof rectWithPadding> | null
) {
  setter((prev) => {
    if (!next && !prev) return prev;
    if (!next || !prev) return next;
    if (
      prev.top === next.top &&
      prev.left === next.left &&
      prev.width === next.width &&
      prev.height === next.height
    ) {
      return prev;
    }
    return next;
  });
}

export function EvWorkstationTour(props: EvWorkstationTourProps) {
  const {
    open,
    onClose,
    viewMode,
    setViewMode,
    lane,
    setLane,
    selectedProjectId,
    setSelectedProjectId,
    hasProjects,
  } = props;

  const [index, setIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<ReturnType<typeof rectWithPadding> | null>(null);
  const [initialState, setInitialState] = useState<EvTourInitialState | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const popoverRef = useRef<HTMLElement>(null);
  const hasOpenedRef = useRef(false);
  const directionRef = useRef<"forward" | "backward" | "init">("init");

  const steps = useMemo(() => getVisibleSteps(hasProjects), [hasProjects]);
  const step = steps[index] ?? steps[0];

  const isLast = index >= steps.length - 1;

  const restoreState = useCallback(() => {
    if (!initialState || isRestoring) return;
    setIsRestoring(true);
    restoreTourState(initialState, {
      setViewMode,
      setLane,
      setSelectedProjectId,
    });
    requestAnimationFrame(() => {
      setIsRestoring(false);
    });
  }, [initialState, isRestoring, setViewMode, setLane, setSelectedProjectId]);

  const closeTour = useCallback(
    (completed = false) => {
      if (completed) setEvWorkstationTourCompleted(true);
      restoreState();
      onClose();
    },
    [onClose, restoreState]
  );

  const prepareStep = useCallback(
    async (targetStep: EvWorkstationTourStep) => {
      const { showed, rect } = await prepareTourStep(targetStep, {
        setViewMode,
        setLane,
      });
      setRectIfChanged(setTargetRect, rect);
      return showed;
    },
    [setViewMode, setLane]
  );

  // Initialize / reset when tour opens or closes
  useEffect(() => {
    if (!open) {
      setIndex(0);
      setTargetRect(null);
      hasOpenedRef.current = false;
      directionRef.current = "init";
      return;
    }
    if (!hasOpenedRef.current) {
      hasOpenedRef.current = true;
      setInitialState(captureInitialState(viewMode, lane, selectedProjectId));
      setViewMode("card");
    }
  }, [open, viewMode, lane, selectedProjectId, setViewMode]);

  // Prepare the current step (scroll, measure, skip missing)
  useEffect(() => {
    if (!open) return;
    const currentStep = steps[index];
    if (!currentStep) return;

    void (async () => {
      const showed = await prepareStep(currentStep);
      if (!showed && currentStep.skipIfMissing) {
        if (directionRef.current === "backward") {
          setIndex((i) => Math.max(i - 1, 0));
        } else {
          if (isLast) {
            closeTour(true);
          } else {
            setIndex((i) => Math.min(i + 1, steps.length - 1));
          }
        }
      }
    })();
  }, [open, index, steps, prepareStep, isLast, closeTour]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeTour(false);
        return;
      }
      if (event.key === "ArrowRight" || event.key === "Enter") {
        event.preventDefault();
        directionRef.current = "forward";
        if (isLast) {
          closeTour(true);
        } else {
          setIndex((i) => Math.min(i + 1, steps.length - 1));
        }
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        directionRef.current = "backward";
        setIndex((i) => Math.max(i - 1, 0));
        return;
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, isLast, steps.length, closeTour]);

  // Re-measure on resize/scroll/mutation
  useEffect(() => {
    if (!open || !step?.target) return;

    function measure() {
      if (!step?.target) return;
      const target =
        getFirstVisibleTarget(step.target) ??
        (step.fallbackTarget ? getFirstVisibleTarget(step.fallbackTarget) : null);
      if (!target) {
        setRectIfChanged(setTargetRect, null);
        return;
      }
      const rect = rectWithPadding(target.getBoundingClientRect(), 10);
      setRectIfChanged(setTargetRect, rect);
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", measure);
      window.addEventListener("scroll", measure, true);
    }

    const observer = typeof document !== "undefined" ? new MutationObserver(measure) : null;
    if (observer && document.body) {
      observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    }

    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && document.body) {
      ro.observe(document.body);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", measure);
        window.removeEventListener("scroll", measure, true);
      }
      observer?.disconnect();
      ro?.disconnect();
    };
  }, [open, step]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional refocus on step change
  useLayoutEffect(() => {
    if (open && popoverRef.current) {
      const firstButton = popoverRef.current.querySelector<HTMLElement>("button:not([disabled])");
      firstButton?.focus();
    }
  }, [open, step?.id]);

  useFocusTrap(popoverRef, open);

  if (!open || !step) return null;

  const popoverStyle = computePopoverStyle(targetRect, step.preferredPlacement, 420, 400);

  return (
    <div className="ev-tour-root" role="presentation">
      {targetRect ? (
        <TourShade rect={targetRect} />
      ) : (
        <div className="ev-tour-shade ev-tour-shade--full" />
      )}
      {targetRect ? (
        <div
          className="ev-tour-highlight"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
        />
      ) : null}

      <section
        ref={popoverRef}
        className="ev-tour-popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ev-tour-title"
        aria-describedby="ev-tour-body"
        style={popoverStyle}
      >
        <div className="ev-tour-popover__eyebrow">
          Step {index + 1} of {steps.length}
        </div>

        <h2 id="ev-tour-title" className="ev-tour-popover__title">
          {step.title}
        </h2>

        <div id="ev-tour-body" className="ev-tour-popover__body">
          <TourContent content={step.content} />
        </div>

        <div className="ev-tour-popover__controls">
          <button
            type="button"
            className="ev-tour-button ev-tour-button--secondary"
            onClick={() => {
              directionRef.current = "backward";
              setIndex((i) => Math.max(i - 1, 0));
            }}
            disabled={index === 0}
          >
            Back
          </button>
          <button
            type="button"
            className="ev-tour-button ev-tour-button--danger"
            onClick={() => closeTour(false)}
          >
            Exit
          </button>
          <button
            type="button"
            className="ev-tour-button ev-tour-button--primary"
            onClick={() => {
              directionRef.current = "forward";
              if (isLast) {
                closeTour(true);
              } else {
                setIndex((i) => Math.min(i + 1, steps.length - 1));
              }
            }}
          >
            {isLast ? "Finish" : "Next"}
          </button>
        </div>
      </section>
    </div>
  );
}
